import { HeaderLegendList } from "@-/dev.improve.haniverse.components.ui.header-scroll";
import { MorphemeTilePresentationProvider } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.presentation";
import { useBrowseTabsContentBottomInset } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs._lib.content-bottom-inset";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { SurfaceDescription, SurfaceTitle } from "@-/dev.improve.haniverse.components.ui.surface";
import {
  WordDetailsTilesRow,
  WordSectionExpansionButton,
} from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.words._components.word-tiles-grid";
import {
  type BatchedWordTileData,
  type GlyphStyle,
  getMorphemeGlyphTileWordGlyphStyle,
  getMorphemeGlyphTileWordPinyinStyle,
  useBatchedWordTileDataFromGraphemes,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.glyphs";
import { WordMorphemeGlyphTileLink } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.link.glyphs";
import { useBrowseMorphemePageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.browse.morpheme.navigator";
import { RootContext } from "@-/dev.improve.haniverse.routes._context";
import {
  BrowseWordsPageContext,
  type WordTileFront,
} from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.words._context";
import { cn } from "@-/com.npmjs.cn";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import filledArray from "@-/com.npmjs.filled-array";
import runes from "@-/com.npmjs.runes2";
import { type LegendListRef, type LegendListRenderItemProps } from "@-/com.npmjs.legendapp__list";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { View, type LayoutChangeEvent } from "@-/com.npmjs.react-native";
import arrayLength from "@-/com.npmjs.extra-array__length";

import {
  getWordsFrequencyRowIndex,
  createWordsFrequencyRowIndex,
  WORD_FREQUENCY_COLUMNS as WORD_COLUMNS,
  type WordFrequencyRow,
} from "#words-frequency-row-index";

import { useCorpusWordsFrequencyData } from "#words-frequency-corpus";
import type { WordsFrequencyData } from "@-/dev.improve.haniverse.data.words-frequency";
import { BrowseWordsSkeleton } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs._components.loading-skeletons";
import { ReleasableBrowseTabBody } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs._lib.focus";
const GAP = 4;
const HORIZONTAL_PADDING = 4;
const HEADER_HEIGHT = 58;
/**
 * Height (px) of the surface's bottom cap row. The glyph rows already carry `pb-0.5` (2px), so the
 * cap only needs the remaining 2px to make the space under the grid equal the `px-1` (4px) the grid
 * gets on its left and right. A taller cap reads as a giant dead band below the last row.
 */
const FOOTER_HEIGHT = 2;
const FOOTER_HEIGHT_WITH_GAP = FOOTER_HEIGHT + GAP;
/** Horizontal padding inside each word row (`px-1`), per side. */
const ROW_SIDE_PADDING = 4;
/**
 * Overdraw budget (px) beyond the viewport. Higher than the Characters list's 100: a word tile
 * commits several `Text` nodes, so the list needs more runway to stay ahead of a fling.
 */
const WORDS_DRAW_DISTANCE = 250;

/**
 * Square tile side length for the current layout. Tiles are `aspect-square w-full`, so their height
 * equals their width — the column width after subtracting the row's side padding and inter-tile gaps.
 * The list reports this (plus row padding) as the fixed row height (via getFixedItemSize).
 */
function computeTileSize(containerWidth: number): number {
  const inner =
    containerWidth - HORIZONTAL_PADDING * 2 - ROW_SIDE_PADDING * 2 - GAP * (WORD_COLUMNS - 1);
  return Math.max(1, Math.floor(inner / WORD_COLUMNS));
}

function glyphRowVerticalPadding(first: boolean): number {
  return (first ? GAP : GAP / 2) + GAP / 2;
}

/**
 * Glyph style per character count for the current tile size. Word tiles size their glyphs from the
 * character count (`getMorphemeGlyphTileWordGlyphStyle`), so a small table keyed by length
 * covers every word in the catalog — computed once per layout, never per tile. One-character words
 * share the grapheme tile path so they match character glyph tiles.
 */
function createGlyphStyleByCharacterCount(tileSize: number): (word: string) => GlyphStyle {
  const cache = new Map<number, GlyphStyle>();
  return (word) => {
    const characterCount = arrayLength(runes(word));
    let style = cache.get(characterCount);
    if (style === undefined) {
      style = getMorphemeGlyphTileWordGlyphStyle({
        characterCount,
        contentHeight: tileSize,
        contentWidth: tileSize,
      });
      cache.set(characterCount, style);
    }
    return style;
  };
}

const WORDS_FREQUENCY_SCROLL_POSITION = { key: "words-frequency" };

export function WordsFrequencyView() {
  return (
    <ReleasableBrowseTabBody fallback={<BrowseWordsSkeleton />}>
      <MorphemeTilePresentationProvider>
        <WordsFrequencyViewCorpusGate />
      </MorphemeTilePresentationProvider>
    </ReleasableBrowseTabBody>
  );
}

function WordsFrequencyViewCorpusGate() {
  // The catalog restricted to the user's word corpus (the bundled object itself when unfiltered,
  // so the default path never waits on a query).
  const wordsFrequencyData = useCorpusWordsFrequencyData();
  if (wordsFrequencyData === undefined) {
    return <BrowseWordsSkeleton />;
  }
  return <WordsFrequencyViewContent wordsFrequencyData={wordsFrequencyData} />;
}

function WordsFrequencyViewContent({
  wordsFrequencyData,
}: {
  wordsFrequencyData: WordsFrequencyData;
}) {
  const browseTabsContentBottomInset = useBrowseTabsContentBottomInset({ trailingGap: GAP });
  const { height: toolbarHeight } = useToolbarGridMetrics();
  const [windowWidth] = useContextSelectors(RootContext, (c) => c.windowDimensions.width);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    const width = event.nativeEvent.layout.width;
    if (width > 0) {
      setContainerWidth((prev) => (prev === width ? prev : width));
    }
  }, []);
  const tileSize = computeTileSize(containerWidth ?? windowWidth);

  const [expandedThresholds, setExpandedThresholds] = useState<ReadonlySet<number>>(
    () => new Set(),
  );
  const toggleSection = useCallback((threshold: number) => {
    setExpandedThresholds((previous) => {
      const next = new Set(previous);
      if (next.has(threshold)) {
        next.delete(threshold);
      } else {
        next.add(threshold);
      }
      return next;
    });
  }, []);
  const rowIndex = useMemo(
    () =>
      expandedThresholds.size === 0
        ? getWordsFrequencyRowIndex(wordsFrequencyData)
        : createWordsFrequencyRowIndex(wordsFrequencyData, expandedThresholds),
    [expandedThresholds, wordsFrequencyData],
  );
  const listRef = useRef<LegendListRef>(null);
  useLayoutEffect(() => {
    listRef.current?.clearCaches({ mode: "sizes" });
  }, [rowIndex, tileSize]);

  // ONE pinyin read for every character in the catalog, handed to each tile as a pre-built slice.
  // Without it every word tile self-resolves a `useQueries` over its characters as it scrolls into
  // view — an rAF-coalesced DB round-trip + re-render per row, which is what left freshly mounted
  // rows blank behind a fling.
  const batchedTileData = useBatchedWordTileDataFromGraphemes(rowIndex.uniqueGraphemes);

  const [wordTileFront] = useContextSelectors(BrowseWordsPageContext, (c) => c.wordTileFront);

  // The tile size is derivable from the layout, so size the glyph up front instead of letting every
  // tile measure itself via `onLayout` — the glyph is `opacity-0` until that measurement lands, and
  // under list recycling the re-measure can never fire, leaving a visibly blank tile. Pinyin fronts
  // need the per-word reading list (long syllables / line count), so they can't share the length cache.
  const getGlyphStyleByCharacterCount = useMemo(
    () => createGlyphStyleByCharacterCount(tileSize),
    [tileSize],
  );
  const getGlyphStyle = useCallback(
    (word: string): GlyphStyle => {
      if (wordTileFront === "pinyin") {
        return getMorphemeGlyphTileWordPinyinStyle({
          contentHeight: tileSize,
          contentWidth: tileSize,
          toneNumbersPinyinList: batchedTileData.getSlice(word).primaryPinyin,
        });
      }
      return getGlyphStyleByCharacterCount(word);
    },
    [batchedTileData, getGlyphStyleByCharacterCount, tileSize, wordTileFront],
  );

  // One navigator for the whole list (its `useMutation`s would otherwise mount per tile).
  const browseMorphemePageNavigator = useBrowseMorphemePageNavigator();
  const pushMutate = browseMorphemePageNavigator.push.mutate;
  const handleNavigate = useCallback(
    (word: string) => {
      pushMutate({ morpheme: word, params: {} });
    },
    [pushMutate],
  );

  const renderItem = useCallback(
    ({ item }: LegendListRenderItemProps<number>) => (
      <RowRenderer
        batchedTileData={batchedTileData}
        expandedThresholds={expandedThresholds}
        front={wordTileFront}
        getGlyphStyle={getGlyphStyle}
        onNavigate={handleNavigate}
        onToggleSection={toggleSection}
        row={rowIndex.getRow(item)}
        tileSize={tileSize}
      />
    ),
    [
      batchedTileData,
      expandedThresholds,
      getGlyphStyle,
      handleNavigate,
      rowIndex,
      tileSize,
      toggleSection,
      wordTileFront,
    ],
  );

  const getFixedItemSize = useCallback(
    (id: number) => {
      const kind = rowIndex.getItemType(id);
      if (kind === "header") {
        return HEADER_HEIGHT;
      }
      if (kind === "footer") {
        return FOOTER_HEIGHT_WITH_GAP;
      }
      const row = rowIndex.getRow(id);
      const size = row.kind === "row" && row.columns === 4 ? tileSize * 2 + GAP : tileSize;
      return size + glyphRowVerticalPadding(rowIndex.isFirstWordRow(id));
    },
    [rowIndex, tileSize],
  );

  // Recycled rows stay mounted across tile-front flips; carry the front in extraData so they
  // re-render when Display switches glyphs ↔ pinyin.
  const rowContent = useMemo(
    () => ({ expandedThresholds, wordTileFront }),
    [expandedThresholds, wordTileFront],
  );

  return (
    <View className="flex-1" onLayout={handleLayout}>
      <HeaderLegendList<number>
        contentContainerStyle={{
          paddingBottom: browseTabsContentBottomInset,
          paddingHorizontal: HORIZONTAL_PADDING,
        }}
        data={rowIndex.rowIds}
        drawDistance={WORDS_DRAW_DISTANCE}
        estimatedItemSize={tileSize + GAP}
        extraData={rowContent}
        getFixedItemSize={getFixedItemSize}
        getItemType={rowIndex.getItemType}
        glassHeaderOverlap={toolbarHeight}
        headerOverlap={toolbarHeight}
        keyExtractor={rowIndex.getKey}
        recycleItems
        ref={listRef}
        renderItem={renderItem}
        // Released while hidden (see `WordsFrequencyView`); reopen at the row the learner left.
        scrollPosition={WORDS_FREQUENCY_SCROLL_POSITION}
        testID="words-frequency-list"
      />
    </View>
  );
}

function RowRenderer({
  batchedTileData,
  expandedThresholds,
  front,
  getGlyphStyle,
  onNavigate,
  onToggleSection,
  row,
  tileSize,
}: {
  batchedTileData: BatchedWordTileData;
  expandedThresholds: ReadonlySet<number>;
  front: WordTileFront;
  getGlyphStyle: (word: string) => GlyphStyle;
  onNavigate: (word: string) => void;
  onToggleSection: (threshold: number) => void;
  row: WordFrequencyRow;
  tileSize: number;
}) {
  if (row.kind === "header") {
    return (
      <View
        className="flex-row items-center justify-between rounded-t-xs border border-b border-border/60 bg-surface-header px-3 pt-3.5 pb-2"
        style={{ height: HEADER_HEIGHT }}
      >
        <View className="flex-1">
          <SurfaceTitle>{row.label}</SurfaceTitle>
          <SurfaceDescription>{row.subtitle}</SurfaceDescription>
        </View>
        <WordSectionExpansionButton
          expanded={expandedThresholds.has(row.threshold)}
          onPress={() => onToggleSection(row.threshold)}
          section={row.label}
        />
      </View>
    );
  }

  if (row.kind === "footer") {
    return (
      <View
        className="rounded-b-xs border border-t-0 border-border/60 bg-surface"
        style={{ height: FOOTER_HEIGHT, marginBottom: GAP }}
      />
    );
  }

  const tileSizeStyle = { height: tileSize, width: tileSize };
  const detailed = row.columns === 4;
  const size = detailed ? tileSize * 2 + GAP : tileSize;

  return (
    <View
      className={cn(
        "flex-row gap-1 border-r border-l border-border/60 bg-surface px-1 pb-0.5",
        row.first ? "pt-1" : "pt-0.5",
      )}
      style={{ height: size + glyphRowVerticalPadding(row.first) }}
    >
      {detailed ? (
        <WordDetailsTilesRow
          onNavigate={onNavigate}
          size={size}
          start={row.wordOffset}
          words={row.words}
        />
      ) : (
        filledArray((colIdx) => {
          const word = row.words[row.wordOffset + colIdx];
          return (
            <View
              key={word === undefined ? `empty-${colIdx}` : `${word}-${colIdx}`}
              style={tileSizeStyle}
            >
              {word === undefined ? null : (
                <WordMorphemeGlyphTileLink
                  front={front}
                  glyphStyle={getGlyphStyle(word)}
                  href="/(nav)/(app)/browse/morpheme/[morpheme]"
                  // Pre-built from the list-level batched read: the tile fires no queries and no
                  // navigator mutations of its own (see `useBatchedWordTileData`).
                  morphemeGlyphTileSlice={batchedTileData.getSlice(word)}
                  onNavigate={onNavigate}
                  word={word}
                />
              )}
            </View>
          );
        }, WORD_COLUMNS)
      )}
    </View>
  );
}
